import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import './LegalPage.css';

const slugify = (s) =>
  String(s)
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');

export default function LegalPage({
  title,
  effectiveDate,
  lastUpdated,
  metaTitle,
  metaDescription,
  children,
}) {
  const { t, lang } = useLanguage();
  const [toc, setToc] = useState([]);

  useEffect(() => {
    const resolvedTitle = metaTitle || `${title} | CosmoEstates`;
    document.title = resolvedTitle;

    let descTag = document.querySelector('meta[name="description"]');
    if (!descTag) {
      descTag = document.createElement('meta');
      descTag.setAttribute('name', 'description');
      document.head.appendChild(descTag);
    }
    const prevDesc = descTag.getAttribute('content');
    if (metaDescription) descTag.setAttribute('content', metaDescription);

    let robotsTag = document.querySelector('meta[name="robots"]');
    const createdRobots = !robotsTag;
    if (!robotsTag) {
      robotsTag = document.createElement('meta');
      robotsTag.setAttribute('name', 'robots');
      document.head.appendChild(robotsTag);
    }
    const prevRobots = robotsTag.getAttribute('content');
    robotsTag.setAttribute('content', 'index, follow');

    let ogTitle = document.querySelector('meta[property="og:title"]');
    const prevOgTitle = ogTitle ? ogTitle.getAttribute('content') : null;
    if (ogTitle) ogTitle.setAttribute('content', resolvedTitle);
    let ogDesc = document.querySelector('meta[property="og:description"]');
    const prevOgDesc = ogDesc ? ogDesc.getAttribute('content') : null;
    if (ogDesc && metaDescription) ogDesc.setAttribute('content', metaDescription);

    return () => {
      if (prevDesc != null) descTag.setAttribute('content', prevDesc);
      if (createdRobots && robotsTag.parentNode) {
        robotsTag.parentNode.removeChild(robotsTag);
      } else if (prevRobots != null) {
        robotsTag.setAttribute('content', prevRobots);
      }
      if (ogTitle && prevOgTitle != null) ogTitle.setAttribute('content', prevOgTitle);
      if (ogDesc && prevOgDesc != null) ogDesc.setAttribute('content', prevOgDesc);
    };
  }, [title, metaTitle, metaDescription]);

  useEffect(() => {
    const body = document.querySelector('.legal-body');
    if (!body) return;
    const headings = Array.from(body.querySelectorAll('h2'));
    const items = headings.map((h) => {
      if (!h.id) h.id = slugify(h.textContent || '');
      return { id: h.id, text: h.textContent || '' };
    });
    setToc(items);
  }, [children]);

  return (
    <main className="legal-page" lang={lang}>
      <article className="legal-page__container">
        <header className="legal-page__header">
          <p className="legal-page__crumb">
            <Link to="/legal">{t('legal.breadcrumb')}</Link>
            <span aria-hidden="true"> / </span>
            <span>{title}</span>
          </p>
          <h1 className="legal-page__title">{title}</h1>
          {(effectiveDate || lastUpdated) && (
            <p className="legal-page__dates">
              {effectiveDate && (
                <span>
                  <strong>{t('legal.effectiveDate')}</strong> {effectiveDate}
                </span>
              )}
              {effectiveDate && lastUpdated && <span className="legal-page__dot"> · </span>}
              {lastUpdated && (
                <span>
                  <strong>{t('legal.lastUpdated')}</strong> {lastUpdated}
                </span>
              )}
            </p>
          )}
        </header>

        {toc.length > 1 && (
          <nav className="legal-page__toc" aria-label={t('legal.onThisPage')}>
            <h2 className="legal-page__toc-title">{t('legal.onThisPage')}</h2>
            <ol>
              {toc.map((item) => (
                <li key={item.id}>
                  <a href={`#${item.id}`}>{item.text}</a>
                </li>
              ))}
            </ol>
          </nav>
        )}

        <div className="legal-body">{children}</div>

        <p className="legal-page__back">
          <Link to="/legal">{t('legal.backToAll')}</Link>
        </p>
      </article>
    </main>
  );
}
